/**
 * Report API Contract
 *
 * Defines the oRPC contract for Reports - saved reports, scheduled delivery,
 * report instances, viewers, viewer groups, comments, and pivot table queries.
 *
 * IMPORTANT SECURITY NOTES:
 * - companyId is NEVER in input schemas - always extracted from JWT context
 * - ListReportsQuerySchema and ListInstancesQuerySchema in shared/types/report.ts
 *   include companyId, but we omit it here (comes from JWT)
 *
 * ROUTE ORDERING:
 * - Static routes (/instances/my, /instances, /pivot-table, /pivot-table/csv)
 *   MUST come before /{reportId} to prevent matching as UUID param
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateSavedReportInputSchema, UpdateSavedReportInputSchema, CreateScheduleInputSchema, UpdateScheduleInputSchema, ReviewInstanceInputSchema, UpdateReviewCommentInputSchema, ReportScheduleFrequencySchema, ReportReviewStatusSchema, RelativeDatePeriodSchema, SavedReportParametersSchema, ConditionalFormattingSchema, AddViewerInputSchema, AddViewerGroupInputSchema, CreateCommentInputSchema, UpdateCommentInputSchema, MyInstancesQuerySchema, } from "@hourly/api/report";
import { PivotTableRequestSchema, } from "@hourly/api/pivotTable";
import { uuidSchema } from "@hourly/api/validation-common";
// ============ Inline Response Schemas ============
// NOTE: shared/types/report.ts defines response types as TypeScript interfaces,
// not Zod schemas. These Zod schemas mirror those interfaces for oRPC output validation.
const SavedReportResponseSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    category: z.string().nullable(),
    conditionalFormatting: ConditionalFormattingSchema.nullable(),
    parameters: SavedReportParametersSchema,
    relativePeriod: RelativeDatePeriodSchema,
    createdByEmploymentId: uuidSchema,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
const ReportScheduleResponseSchema = z.object({
    id: uuidSchema,
    savedReportId: uuidSchema,
    companyId: uuidSchema,
    recipientEmploymentIds: z.array(uuidSchema),
    recipientTagIds: z.array(uuidSchema),
    frequency: ReportScheduleFrequencySchema,
    anchorDate: z.iso.datetime().nullable(),
    isActive: z.boolean(),
    nextDeliveryTime: z.iso.datetime().nullable(),
    lastDeliveredAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
const SavedReportWithScheduleResponseSchema = SavedReportResponseSchema.extend({
    schedule: ReportScheduleResponseSchema.nullable(),
});
// PivotTableRequest shape (used as parametersSnapshot in instances)
const PivotTableRequestSnapshotSchema = z.record(z.string(), z.unknown());
const ReportInstanceResponseSchema = z.object({
    id: uuidSchema,
    savedReportId: uuidSchema,
    reportScheduleId: uuidSchema.nullable(),
    companyId: uuidSchema,
    assigneeEmploymentId: uuidSchema,
    assigneeLocationId: uuidSchema,
    parametersSnapshot: PivotTableRequestSnapshotSchema,
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime(),
    deliveredAt: z.iso.datetime(),
    reviewStatus: ReportReviewStatusSchema,
    reviewedAt: z.iso.datetime().nullable(),
    reviewedByEmploymentId: uuidSchema.nullable(),
    reviewComment: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
const ReportInstanceWithDetailsResponseSchema = ReportInstanceResponseSchema.extend({
    savedReport: z.object({
        id: uuidSchema,
        name: z.string(),
    }),
    reviewedByEmployment: z
        .object({
        id: uuidSchema,
        user: z.object({
            firstName: z.string(),
            lastName: z.string(),
        }),
    })
        .nullable(),
});
const PaginatedReportInstanceResponseSchema = z.object({
    data: z.array(ReportInstanceWithDetailsResponseSchema),
    pagination: z.object({
        page: z.number().int(),
        limit: z.number().int(),
        total: z.number().int(),
        pages: z.number().int(),
    }),
});
const ViewerUserResponseSchema = z.object({
    id: uuidSchema,
    savedReportId: uuidSchema,
    employmentId: uuidSchema,
    createdAt: z.iso.datetime(),
});
const ViewerGroupResponseSchema = z.object({
    id: uuidSchema,
    savedReportId: uuidSchema,
    tagGroupId: uuidSchema,
    createdAt: z.iso.datetime(),
});
const ListViewersResponseSchema = z.object({
    users: z.array(ViewerUserResponseSchema),
    groups: z.array(ViewerGroupResponseSchema),
});
const CommentResponseSchema = z.object({
    id: uuidSchema,
    reportInstanceId: uuidSchema,
    authorEmploymentId: uuidSchema,
    content: z.string(),
    isReviewComment: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    authorEmployment: z
        .object({
        user: z.object({
            firstName: z.string(),
            lastName: z.string(),
        }),
    })
        .optional(),
});
// ============ Pivot Table Response Schema ============
// NOTE: PivotTableResponse is a TypeScript interface in shared/types/pivotTable.ts.
// Using z.unknown() for deeply nested recursive structures.
const PivotTableResponseSchema = z.object({
    metadata: z.object({
        seriesTypes: z.array(z.string()),
        aggregationMethod: z.string(),
        dateAggregation: z.string(),
        dateRange: z.object({ start: z.string(), end: z.string() }),
        dateBuckets: z.array(z.string()),
        seriesMetadata: z.record(z.string(), z.object({
            displayName: z.string(),
            unit: z.string(),
        })),
        hierarchyLevels: z.array(z.string()),
    }),
    nodes: z.array(z.unknown()),
    totals: z.record(z.string(), z.record(z.string(), z.number())),
});
// ============ Query Schemas (companyId omitted - comes from JWT) ============
const ListReportsQuerySchema = z.object({
    createdByEmploymentId: uuidSchema.optional(),
    includeDeleted: z.coerce.boolean().optional(),
});
const ListInstancesQuerySchema = z.object({
    savedReportId: uuidSchema.optional(),
    reviewStatus: ReportReviewStatusSchema.optional(),
    assigneeEmploymentId: uuidSchema.optional(),
    page: z.coerce.number().int().positive().optional().default(1),
    limit: z.coerce.number().int().positive().max(100).optional().default(50),
});
// ============ Group 1: Saved Reports ============
// POST /reports
const createSavedReport = oc
    .route({
    method: "POST",
    path: "/reports",
    summary: "Create a new saved report",
    tags: ["Reports"],
    successStatus: 201,
})
    .input(CreateSavedReportInputSchema)
    .output(SavedReportResponseSchema);
// GET /reports
const listSavedReports = oc
    .route({
    method: "GET",
    path: "/reports",
    summary: "List saved reports for the company",
    tags: ["Reports"],
})
    .input(ListReportsQuerySchema)
    .output(z.array(SavedReportWithScheduleResponseSchema));
// GET /reports/{reportId}
const getSavedReport = oc
    .route({
    method: "GET",
    path: "/reports/{reportId}",
    summary: "Get a saved report by ID",
    tags: ["Reports"],
})
    .input(z.object({ reportId: uuidSchema }))
    .output(SavedReportWithScheduleResponseSchema);
// PATCH /reports/{reportId}
const updateSavedReport = oc
    .route({
    method: "PATCH",
    path: "/reports/{reportId}",
    summary: "Update a saved report",
    tags: ["Reports"],
})
    .input(z.object({ reportId: uuidSchema }).merge(UpdateSavedReportInputSchema))
    .output(SavedReportResponseSchema);
// DELETE /reports/{reportId}
const deleteSavedReport = oc
    .route({
    method: "DELETE",
    path: "/reports/{reportId}",
    summary: "Delete a saved report",
    tags: ["Reports"],
    successStatus: 204,
})
    .input(z.object({ reportId: uuidSchema }))
    .output(z.void());
// ============ Group 2: Report Schedules ============
// POST /reports/{reportId}/schedule
const createSchedule = oc
    .route({
    method: "POST",
    path: "/reports/{reportId}/schedule",
    summary: "Create a delivery schedule for a saved report",
    tags: ["Reports"],
    successStatus: 201,
})
    .input(z.object({ reportId: uuidSchema }).merge(CreateScheduleInputSchema))
    .output(ReportScheduleResponseSchema);
// GET /reports/{reportId}/schedule
const getSchedule = oc
    .route({
    method: "GET",
    path: "/reports/{reportId}/schedule",
    summary: "Get the delivery schedule for a saved report",
    tags: ["Reports"],
})
    .input(z.object({ reportId: uuidSchema }))
    .output(ReportScheduleResponseSchema.nullable());
// PATCH /reports/{reportId}/schedule
const updateSchedule = oc
    .route({
    method: "PATCH",
    path: "/reports/{reportId}/schedule",
    summary: "Update the delivery schedule for a saved report",
    tags: ["Reports"],
})
    .input(z.object({ reportId: uuidSchema }).merge(UpdateScheduleInputSchema))
    .output(ReportScheduleResponseSchema);
// DELETE /reports/{reportId}/schedule
const deactivateSchedule = oc
    .route({
    method: "DELETE",
    path: "/reports/{reportId}/schedule",
    summary: "Deactivate the delivery schedule for a saved report",
    tags: ["Reports"],
    successStatus: 204,
})
    .input(z.object({ reportId: uuidSchema }))
    .output(z.void());
// ============ Group 3: Report Delivery ============
// POST /reports/{reportId}/deliver
const deliverReportNow = oc
    .route({
    method: "POST",
    path: "/reports/{reportId}/deliver",
    summary: "Deliver a saved report immediately",
    tags: ["Reports"],
    successStatus: 201,
})
    .input(z.object({ reportId: uuidSchema }))
    .output(ReportInstanceResponseSchema);
// ============ Group 4: Report Instances ============
// NOTE: Static sub-paths (/instances/my, /instances) must be registered before
// /{reportId} in the router to prevent UUID param conflicts.
// GET /reports/instances/my
const getMyInstances = oc
    .route({
    method: "GET",
    path: "/reports/instances/my",
    summary: "Get report instances assigned to the current user",
    tags: ["Reports"],
})
    .input(MyInstancesQuerySchema)
    .output(z.array(ReportInstanceWithDetailsResponseSchema));
// GET /reports/instances
const listInstances = oc
    .route({
    method: "GET",
    path: "/reports/instances",
    summary: "List all report instances for the company (manager view)",
    tags: ["Reports"],
})
    .input(ListInstancesQuerySchema)
    .output(PaginatedReportInstanceResponseSchema);
// GET /reports/instances/{instanceId}
const getInstance = oc
    .route({
    method: "GET",
    path: "/reports/instances/{instanceId}",
    summary: "Get a report instance by ID",
    tags: ["Reports"],
})
    .input(z.object({ instanceId: uuidSchema }))
    .output(ReportInstanceWithDetailsResponseSchema);
// POST /reports/instances/{instanceId}/review
const reviewInstance = oc
    .route({
    method: "POST",
    path: "/reports/instances/{instanceId}/review",
    summary: "Mark a report instance as reviewed",
    tags: ["Reports"],
})
    .input(z.object({ instanceId: uuidSchema }).merge(ReviewInstanceInputSchema))
    .output(ReportInstanceWithDetailsResponseSchema);
// PATCH /reports/instances/{instanceId}/review
const updateReviewComment = oc
    .route({
    method: "PATCH",
    path: "/reports/instances/{instanceId}/review",
    summary: "Update the review comment on a report instance",
    tags: ["Reports"],
})
    .input(z
    .object({ instanceId: uuidSchema })
    .merge(UpdateReviewCommentInputSchema))
    .output(ReportInstanceWithDetailsResponseSchema);
// ============ Group 5: Report Viewers ============
// POST /reports/{reportId}/viewers
const addViewer = oc
    .route({
    method: "POST",
    path: "/reports/{reportId}/viewers",
    summary: "Add an employment as a viewer of a saved report",
    tags: ["Reports"],
    successStatus: 201,
})
    .input(z.object({ reportId: uuidSchema }).merge(AddViewerInputSchema))
    .output(ViewerUserResponseSchema);
// GET /reports/{reportId}/viewers
const listViewers = oc
    .route({
    method: "GET",
    path: "/reports/{reportId}/viewers",
    summary: "List all viewers of a saved report",
    tags: ["Reports"],
})
    .input(z.object({ reportId: uuidSchema }))
    .output(ListViewersResponseSchema);
// DELETE /reports/{reportId}/viewers/{employmentId}
const removeViewer = oc
    .route({
    method: "DELETE",
    path: "/reports/{reportId}/viewers/{employmentId}",
    summary: "Remove an employment from the viewer list of a saved report",
    tags: ["Reports"],
    successStatus: 204,
})
    .input(z.object({ reportId: uuidSchema, employmentId: uuidSchema }))
    .output(z.void());
// ============ Group 6: Viewer Groups ============
// POST /reports/{reportId}/viewer-groups
const addViewerGroup = oc
    .route({
    method: "POST",
    path: "/reports/{reportId}/viewer-groups",
    summary: "Add a tag group as a viewer group for a saved report",
    tags: ["Reports"],
    successStatus: 201,
})
    .input(z.object({ reportId: uuidSchema }).merge(AddViewerGroupInputSchema))
    .output(ViewerGroupResponseSchema);
// DELETE /reports/{reportId}/viewer-groups/{tagGroupId}
const removeViewerGroup = oc
    .route({
    method: "DELETE",
    path: "/reports/{reportId}/viewer-groups/{tagGroupId}",
    summary: "Remove a tag group from the viewer groups of a saved report",
    tags: ["Reports"],
    successStatus: 204,
})
    .input(z.object({ reportId: uuidSchema, tagGroupId: uuidSchema }))
    .output(z.void());
// ============ Group 7: Report Comments ============
// POST /reports/instances/{instanceId}/comments
const addComment = oc
    .route({
    method: "POST",
    path: "/reports/instances/{instanceId}/comments",
    summary: "Add a comment to a report instance",
    tags: ["Reports"],
    successStatus: 201,
})
    .input(z.object({ instanceId: uuidSchema }).merge(CreateCommentInputSchema))
    .output(CommentResponseSchema);
// GET /reports/instances/{instanceId}/comments
const listComments = oc
    .route({
    method: "GET",
    path: "/reports/instances/{instanceId}/comments",
    summary: "List all comments on a report instance",
    tags: ["Reports"],
})
    .input(z.object({ instanceId: uuidSchema }))
    .output(z.array(CommentResponseSchema));
// PATCH /reports/instances/{instanceId}/comments/{commentId}
const updateComment = oc
    .route({
    method: "PATCH",
    path: "/reports/instances/{instanceId}/comments/{commentId}",
    summary: "Update a comment on a report instance",
    tags: ["Reports"],
})
    .input(z
    .object({ instanceId: uuidSchema, commentId: uuidSchema })
    .merge(UpdateCommentInputSchema))
    .output(CommentResponseSchema);
// DELETE /reports/instances/{instanceId}/comments/{commentId}
const deleteComment = oc
    .route({
    method: "DELETE",
    path: "/reports/instances/{instanceId}/comments/{commentId}",
    summary: "Delete a comment on a report instance",
    tags: ["Reports"],
    successStatus: 204,
})
    .input(z.object({ instanceId: uuidSchema, commentId: uuidSchema }))
    .output(z.void());
// ============ Group 8: Pivot Table ============
// POST /reports/pivot-table
const queryPivotTable = oc
    .route({
    method: "POST",
    path: "/reports/pivot-table",
    summary: "Query the pivot table with dynamic grouping and aggregation",
    tags: ["Reports"],
})
    .input(PivotTableRequestSchema)
    .output(PivotTableResponseSchema);
// POST /reports/pivot-table/csv
const downloadPivotTableCsv = oc
    .route({
    method: "POST",
    path: "/reports/pivot-table/csv",
    summary: "Download pivot table data as CSV",
    tags: ["Reports"],
})
    .input(PivotTableRequestSchema)
    .output(z.string());
// ============ Export Contract ============
export const reportContract = {
    // Saved Reports
    createSavedReport,
    listSavedReports,
    getSavedReport,
    updateSavedReport,
    deleteSavedReport,
    // Report Schedules
    createSchedule,
    getSchedule,
    updateSchedule,
    deactivateSchedule,
    // Report Delivery
    deliverReportNow,
    // Report Instances (static paths before dynamic /{reportId})
    getMyInstances,
    listInstances,
    getInstance,
    reviewInstance,
    updateReviewComment,
    // Report Viewers
    addViewer,
    listViewers,
    removeViewer,
    // Viewer Groups
    addViewerGroup,
    removeViewerGroup,
    // Report Comments
    addComment,
    listComments,
    updateComment,
    deleteComment,
    // Pivot Table
    queryPivotTable,
    downloadPivotTableCsv,
};
//# sourceMappingURL=report.js.map